import { useGetAllDepartmentsQuery } from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
import {
  useBulkRedlistApplicantMutation,
  useGetAllOfferedApplicantsQuery,
  useUpdateHiringPipelineStatusMutation,
} from "@/app/features/recruitment/hiring-pipeline/hiring-pipeline.api";
import { useGetAllJobPostsQuery } from "@/app/features/recruitment/job-applicants/job-applicants.api";
import ConfirmationModal from "@/components/ConfirmationModal";
import EmptyState from "@/components/EmptyState";
import ViewToggler from "@/components/ViewToggler";
import WithLoader from "@/components/WithLoader";
import { FILTER_KEYS } from "@/constants/keys.constants";
import { useAppFilters, useSelectionOptions } from "@/hooks";
import useDiscloser from "@/hooks/useDiscloser";
import {
  CheckIcon,
  ChevronDownIcon,
  PaperclipIcon,
  SearchIcon,
  UserMultipleIcon,
} from "@/icons";
import { cn } from "@/lib/cn";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { firstChar } from "@/utils/string.utils";
import { Button, Flex, Input, Select } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useState } from "react";
import EmpCard from "../Evaluation/_components/EvAssessment/EmpCard";
import OfferLetterDrawer from "./_components/OfferLetterDrawer";

export default function Hired() {
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmStatus, setConfirmStatus] = useState<
    "red_list" | "cancel" | null
  >(null);
  const [reason, setReason] = useState("");
  const [drawerConfig, openDrawer, closeDrawer] = useDiscloser();

  const { page, limit, keyword, filters, handleFilter, handleSearch } =
    useAppFilters(FILTER_KEYS.HIRING_PIPELINE);
  const { select_status, ...others } = filters || {};

  // ── Options ────────────────────────────────────────────────────────────────
  const [jobOptions, isJobsFetching] = useSelectionOptions(
    useGetAllJobPostsQuery,
    {
      labelKey: "job_title",
      valueKey: "id",
    },
  );

  const [departmentOptions, isDeptFetching] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: "name",
      valueKey: "id",
    },
  );

  // ── Data ───────────────────────────────────────────────────────────────────
  const {
    data: hiredData,
    isLoading,
    isFetching,
  } = useGetAllOfferedApplicantsQuery({
    params: {
      page,
      limit,
      search: keyword,
      hiring_pipeline_status: "hired",
      ...others,
    },
  });

  const applicants = hiredData?.data?.data || [];

  // ── Mutation ───────────────────────────────────────────────────────────────
  const [updateHiringStatus, { isLoading: isMoving }] =
    useUpdateHiringPipelineStatusMutation();
  const [bulkRedlist, { isLoading: isRedlisting }] =
    useBulkRedlistApplicantMutation();

  const handleUpdateStatus = async () => {
    if (!selected.length) return toast("error", "Please select applicants.");
    if (!reason.trim()) return toast("error", "Please provide a reason.");

    try {
      if (confirmStatus === "red_list") {
        const selectedApplicants = applicants.filter((a) =>
          selected.includes(a.offer_letter_id),
        );

        await bulkRedlist({
          items: selectedApplicants.map((a) => ({
            applicant_id: a.applicant_id,
            email: a.applicant_email,
            nid: a.applicant_nid || "",
            reason: reason.trim(),
          })),
        }).unwrap();

        await updateHiringStatus({
          offerLetterIds: selected,
          hiring_pipeline_status: "red_list",
          hiring_pipeline_red_list_reason: reason,
        }).unwrap();
      } else {
        await updateHiringStatus({
          offerLetterIds: selected,
          hiring_pipeline_status: confirmStatus,
          hiring_pipeline_cancel_reason: reason,
        }).unwrap();
      }

      toast(
        "success",
        confirmStatus === "red_list"
          ? "Applicants moved to Red List successfully"
          : "Applicants moved to Canceled successfully",
      );
      handleCloseModal();
      setSelected([]);
      handleFilter("select_status", null);
    } catch (error) {
      toast("error", getErrorMessage(error, "Failed to move applicants"));
    }
  };

  const handleCloseModal = () => {
    setConfirmStatus(null);
    setReason("");
  };

  // ── Selection ──────────────────────────────────────────────────────────────
  const handleSelect = (id: string) => {
    if (!select_status) return;
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    setSelected([]);
  }, [select_status]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Flex
        align="start"
        justify="space-between"
        gap={16}
        className="flex-wrap lg:flex-nowrap"
      >
        <ViewToggler disabled="list" mode="grid" setMode={() => {}} />
        <Flex vertical gap={12} align="end">
          <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
            <Input
              allowClear
              size="large"
              onChange={handleSearch}
              placeholder="Search Applicant"
              prefix={<SearchIcon size={20} />}
            />
            <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Job Position"
                popupMatchSelectWidth={false}
                loading={isJobsFetching as boolean}
                onChange={(value) => handleFilter("job_id", value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={jobOptions as any[]}
              />
            </div>
            <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Department"
                popupMatchSelectWidth={false}
                loading={isDeptFetching as boolean}
                onChange={(value) => handleFilter("department_id", value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={departmentOptions as any[]}
              />
            </div>
          </Flex>

          {!select_status ? (
            <Flex gap={16}>
              <Button
                size="large"
                onClick={() => handleFilter("select_status", "cancel")}
                className="btn-primary-bordered"
              >
                Select For Canceled
              </Button>
              <Button
                size="large"
                onClick={() => handleFilter("select_status", "red-list")}
                className="btn-primary-bordered"
              >
                Select For Red List
              </Button>
            </Flex>
          ) : (
            <Flex gap={16}>
              <Button
                size="large"
                disabled={isMoving}
                onClick={() => handleFilter("select_status", null)}
                className="btn-primary-bordered min-w-24"
              >
                Cancel
              </Button>
              <Button
                size="large"
                disabled={isMoving}
                onClick={() => {
                  if (!selected.length) {
                    toast("error", "Please select applicants.");
                    return;
                  }
                  setConfirmStatus(
                    select_status === "red-list" ? "red_list" : "cancel",
                  );
                }}
                className="btn-primary min-w-32"
              >
                Move to {select_status === "red-list" ? "Red List" : "Canceled"}
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Cards */}
      <WithLoader isLoading={isLoading || isFetching}>
        {applicants.length === 0 ? (
          <EmptyState
            icon={<UserMultipleIcon size={40} />}
            title="No Hired Applicants"
            description="No applicants have been hired yet, or none match your current filters."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {applicants.map((u) => {
              const isSelected = selected.includes(u.offer_letter_id);
              return (
                <div
                  key={u.offer_letter_id}
                  onClick={() => handleSelect(u.offer_letter_id)}
                  className={cn(
                    "relative transition-all rounded-xl duration-200 text-left",
                    select_status ? "cursor-pointer" : "cursor-auto",
                    isSelected && "ring ring-theme-primary",
                  )}
                >
                  <Flex
                    align="center"
                    justify="center"
                    className={cn(
                      "absolute -top-3 -right-3 w-7 h-7 bg-theme-primary rounded-full transition-all duration-200 z-10",
                      isSelected
                        ? "visible opacity-100"
                        : "invisible opacity-0",
                    )}
                  >
                    <CheckIcon className="text-xl text-theme-slate-700" />
                  </Flex>
                  <EmpCard
                    showStatus={false}
                    item={{
                      name: u.applicant_name,
                      photo: u.photo,
                      designation: u.job_title,
                      department: u.department_name,
                      assessment_status: u.offer_letter_status,
                    }}
                    actionRender={
                      <div className="space-y-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDrawer({ data: u.offer_letter_id });
                          }}
                          className="text-theme-primary font-semibold cursor-pointer underline underline-offset-2"
                        >
                          View Offer Letter
                        </button>
                        {u.cv_link && (
                          <Flex
                            gap={4}
                            align="center"
                            justify="center"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(u.cv_link, "_blank");
                            }}
                            className="w-full p-1 bg-theme-gray-90 rounded text-theme-slate-800 text-sm cursor-pointer"
                          >
                            <PaperclipIcon
                              size={14}
                              className="text-[#007FC4]"
                            />
                            {firstChar(u.cv_link.split("/").pop() ?? "CV")}
                            ...pdf
                          </Flex>
                        )}
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      </WithLoader>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={!!confirmStatus}
        loading={isMoving || isRedlisting}
        title="Are you sure?"
        buttonName="Proceed"
        handleSubmit={handleUpdateStatus}
        handleClose={handleCloseModal}
        subTitle={`You want to ${confirmStatus === "red_list" ? "Red List" : "Cancel"} them from hiring process`}
      >
        <div className="mt-4 space-y-1">
          <label className="text-white text-lg font-normal w-full">
            Reason
          </label>
          <TextArea
            rows={4}
            value={reason}
            placeholder="Write here..."
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </ConfirmationModal>

      {/* Offer Letter Drawer */}
      <OfferLetterDrawer
        open={drawerConfig.open}
        onClose={closeDrawer}
        offerLetterId={drawerConfig.data}
      />
    </div>
  );
}
